/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import ShareCardModel from '../../../../models/ShareCardModel';

import { Card, CardRowUI, Icons, Spacer } from '../../../../bphComponents/bundle';
import { HECardTitle } from './CardTitle';
import COLORS from '../../../styles/Colors';
import ShareCardIncludedValueToggleSwitch from '../../../components/ShareCardIncludedValueToggleSwitch';
import { useShareCardListForActiveProfile } from '../../../hooks/useShareCard';
import { useActiveUser } from '../../../hooks/useUser';

interface iShareCardsForOccurrence {
  sharecard: any;
  title: string;
  includedType: string;
  isExcluded: boolean;
  isRequired: boolean;
  isIncluded: boolean;
  cssColor: any;
}

interface OccurrenceProps {
  shareCardsForOccurrence: iShareCardsForOccurrence[];
  includedCount: number;
  allowSettingIncludedValues: boolean;
  isDefaultExpanded: boolean;
  onToggle(sc): void;
  onToggleIncludedType(sc): void;
}
const OccurrenceShareCards: React.FC<OccurrenceProps> = (props) => {
  const { allowSettingIncludedValues, isDefaultExpanded, shareCardsForOccurrence, includedCount, onToggle, onToggleIncludedType } = props;
  const [isExpanded, setIsExpanded] = React.useState(isDefaultExpanded);

  return (
    <>
    <HECardTitle>
      <FormattedMessage id="healthEntry.addToShareCards.name.text" />
      <Icons.IconShare.Small color={COLORS.gsMedDark} />
    </HECardTitle>

    <Card.CardShape>
      {
        isExpanded ? (
          shareCardsForOccurrence.map((sco: iShareCardsForOccurrence) => {
            const { sharecard, title, cssColor, includedType, isExcluded, isIncluded, isRequired } = sco;
            const isExpanded = allowSettingIncludedValues && includedType;

            if (isExcluded) { return null; }

            const activeColor = isIncluded ? 'font-color-gsDarkest' : 'font-color-gsDark';

            return (
              <Card.CardSection key={sharecard.id} hasBottomBorder>
                <CardRowUI.SelectableRow
                  isSelected={isIncluded}
                  hasBottomBorder={false}
                  disabledPadding={isExpanded ? ['bottom'] : null}
                  selectedColor={cssColor}
                  notSelectedColor={cssColor}
                  style={{}}
                  onClick={
                    isRequired
                    ? null
                    : isExpanded
                      ? null
                      : () => { onToggle(sharecard); }
                  }
                  onCircleClick={
                    isRequired
                    ? null
                    : isExpanded
                      ? () => { onToggle(sharecard); }
                      : null
                  }
                >
                  <div className={classnames('font-body', activeColor)}>{title}</div>
                </CardRowUI.SelectableRow>

                {
                  isExpanded && (
                    <>
                      <Spacer.Height12 />
                      <Card.CardContent disabledPadding={['top']}>
                        <ShareCardIncludedValueToggleSwitch
                          uniqueID={`${sharecard.id}`}
                          value={includedType}
                          isDisabled={isRequired}
                          onToggle={() => {
                            onToggleIncludedType(sharecard);
                          }}
                        />
                      </Card.CardContent>
                    </>
                  )
                }
              </Card.CardSection>

            );
          })
        ) : (
          <Card.CardContent>
            <Card.CollapsibleControl
              onClick={() => { setIsExpanded(true); }}
            >
              <FormattedMessage id="healthEntry.shareCardsSummary.button.label" values={{ digit1: includedCount }} />
            </Card.CollapsibleControl>
          </Card.CardContent>
        )
      }
    </Card.CardShape>
    </>
  );
};

interface HEShareCardsProps {
  healthEntry: any;
  allowSettingIncludedValues: boolean;
  shareCardsDefaultExpanded: boolean;
}
const HEShareCards: React.FC<HEShareCardsProps> = (props) => {
  const { healthEntry, allowSettingIncludedValues, shareCardsDefaultExpanded } = props;
  const [{ preferredLanguageCode }] = useActiveUser();

  const [{ shareCardList, shareCardDefinitionLookup, isLoaded }, scActions] = useShareCardListForActiveProfile();

  if (!isLoaded) { return null; }

  let includedCount = 0;

  const shareCardsForOccurrence = shareCardList.filter(sc => shareCardDefinitionLookup[sc.definition_id]).map(sc => {
    const includedValue = ShareCardModel.getIsIncluded(sc, healthEntry.id);
    const shareCardColorModel = scActions.getShareCardColor(sc);
    const definition = shareCardDefinitionLookup[sc.definition_id];
    const isIncluded = Boolean(includedValue);
    if (isIncluded) {
      includedCount++;
    }
    return {
      sharecard: sc,
      title: ShareCardModel.getTitle(sc, definition, preferredLanguageCode),
      includedType: includedValue ? includedValue.included_values : null,
      isExcluded: ShareCardModel.getIsExcluded(definition, healthEntry.category, healthEntry[healthEntry.category].type),
      isRequired: ShareCardModel.getIsRequired(definition, healthEntry.category, healthEntry[healthEntry.category].type),
      isIncluded,
      cssColor: shareCardColorModel.cssColor(),
    };
  });

  return (
    <OccurrenceShareCards
      shareCardsForOccurrence={shareCardsForOccurrence}
      includedCount={includedCount}
      allowSettingIncludedValues={allowSettingIncludedValues}
      isDefaultExpanded={shareCardsDefaultExpanded}
      onToggle={(sc) => {
        const definition = shareCardDefinitionLookup[sc.definition_id];
        const newData = ShareCardModel.toggleIncluded(sc, definition, healthEntry);
        scActions.apiUpdate(newData);
      }}
      onToggleIncludedType={(sc) => {
        const newData = ShareCardModel.toggleIncludedType(sc, healthEntry.id);
        scActions.apiUpdate(newData);
      }}
    />
  );
};

interface TrackableShareCardsProps {
  trackable: any;
  shareCardsDefaultExpanded: boolean;
}
export const TrackableShareCards: React.FC<TrackableShareCardsProps> = (props) => {
  const { trackable, shareCardsDefaultExpanded } = props;
  const [{ preferredLanguageCode }] = useActiveUser();
  const trackableId = trackable.id;

  const [{ shareCardList, shareCardDefinitionLookup, isLoaded }, scActions] = useShareCardListForActiveProfile();

  if (!isLoaded) { return null; }

  let includedCount = 0;

  const shareCardsForOccurrence = shareCardList.map(sc => {
    const includedValue = ShareCardModel.getIsTrackableIncluded(sc, trackableId);
    const shareCardColorModel = scActions.getShareCardColor(sc);
    const definition = shareCardDefinitionLookup[sc.definition_id];
    const isIncluded = Boolean(includedValue);
    if (isIncluded) {
      includedCount++;
    }

    return {
      sharecard: sc,
      title: ShareCardModel.getTitle(sc, definition, preferredLanguageCode),
      includedType: includedValue ? includedValue.included_values : null,
      isExcluded: false, // ShareCardModel.getIsExcluded(definition, healthEntry.category, healthEntry[healthEntry.category].type),
      isRequired: false, // ShareCardModel.getIsRequired(definition, healthEntry.category, healthEntry[healthEntry.category].type),
      isIncluded,
      cssColor: shareCardColorModel.cssColor(),
    };
  });

  return (
    <OccurrenceShareCards
      shareCardsForOccurrence={shareCardsForOccurrence}
      includedCount={includedCount}
      allowSettingIncludedValues={true}
      isDefaultExpanded={shareCardsDefaultExpanded}
      onToggle={(sc) => {
        const definition = shareCardDefinitionLookup[sc.definition_id];
        const newData = ShareCardModel.toggleTrackableIncluded(sc, definition, trackableId);
        scActions.apiUpdate(newData);
      }}
      onToggleIncludedType={(sc) => {
        const newData = ShareCardModel.toggleTrackableIncludedType(sc, trackableId);
        scActions.apiUpdate(newData);
      }}
    />
  );
};

export default HEShareCards;
